import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/site";

/**
 * Reusable SEO component. Injects title, description, OG, canonical and
 * optional JSON-LD schema into the document head.
 */
export default function SEO({
    title,
    description,
    path = "",
    image,
    schema,
    noindex = false,
}) {
    const fullTitle = title || `${SITE.name} | Best Music Classes in Malviya Nagar, South Delhi`;
    const desc = description || "Premium Western music and performing arts academy in Malviya Nagar, South Delhi. Piano, guitar, vocals, drums, music production, DJ and dance classes. Book a free trial.";
    const url = `${SITE.url}${path}`;
    const ogImage = image || "https://customer-assets.emergentagent.com/job_ihao-music-house/artifacts/0z27e0qg_ChatGPT%20Image%20Apr%2010%2C%202026%2C%2007_16_37%20PM.png";

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            <link rel="canonical" href={url} />

            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={ogImage} />
            <meta property="og:site_name" content={SITE.name} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />

            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
}

// ---------- Schema builders ----------

export const localBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": ["MusicSchool", "LocalBusiness"],
    "name": SITE.name,
    "image": SITE.url + "/og-image.jpg",
    "@id": SITE.url,
    "url": SITE.url,
    "telephone": SITE.phoneIntl,
    "email": SITE.email,
    "priceRange": SITE.priceRange,
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "Opp DLF Avenue Saket (Select Citywalk), S27 Basement",
        "addressLocality": SITE.addressLocality,
        "addressRegion": SITE.addressRegion,
        "postalCode": SITE.postal,
        "addressCountry": SITE.country,
    },
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": SITE.geo.lat,
        "longitude": SITE.geo.lng,
    },
    "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "10:00",
        "closes": "20:00",
    }],
    "sameAs": [SITE.instagram, SITE.youtube, SITE.whatsapp],
    "foundingDate": SITE.founded,
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "120",
    },
});

export const courseSchema = (course) => ({
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${course.name} Classes`,
    "description": course.metaDesc || course.blurb,
    "provider": {
        "@type": "Organization",
        "name": SITE.name,
        "sameAs": SITE.url,
    },
    "courseMode": "onsite",
    "inLanguage": "en-IN",
    "offers": {
        "@type": "Offer",
        "category": "Paid",
        "availability": "https://schema.org/InStock",
    },
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": {
            "@type": "Answer",
            "text": f.a,
        },
    })),
});

export const reviewSchema = (testimonials) => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE.name,
    "review": testimonials.map((t) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": t.name },
        "reviewRating": { "@type": "Rating", "ratingValue": t.rating, "bestRating": 5 },
        "reviewBody": t.quote,
    })),
});

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": it.name,
        "item": `${SITE.url}${it.path}`,
    })),
});

export const articleSchema = (post) => ({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDesc,
    "image": post.image,
    "datePublished": post.date,
    "author": { "@type": "Organization", "name": SITE.name },
    "publisher": {
        "@type": "Organization",
        "name": SITE.name,
        "logo": { "@type": "ImageObject", "url": `${SITE.url}/logo.png` },
    },
});
